import { useState, useEffect } from 'react'
import { isWebView } from '@/utils/webView'
import { verifyAppPasscode } from '@/utils/webViewBridge'
import { useAppStore } from '@/store/appStore'

interface PasscodeScreenProps {
  onSuccess: () => void
}

export default function PasscodeScreen({ onSuccess }: PasscodeScreenProps) {
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState(false)
  const { appPassword } = useAppStore()

  const handlePasscodeInput = (value: string) => {
    if (value.length <= 4 && /^\d*$/.test(value)) {
      setPasscode(value)
      setError(false)
    }
  }

  const verifyPasscode = async () => {
    if (isWebView()) {
      // 웹뷰 환경: 네이티브 패스코드 확인
      try {
        const result = await verifyAppPasscode()
        if (result) {
          onSuccess()
        } else {
          setError(true)
          setPasscode('')
        }
      } catch (error) {
        console.error('패스코드 확인 실패:', error)
        setError(true)
      }
    } else {
      // 웹 환경: Zustand store에서 확인
      if (appPassword === passcode) {
        onSuccess()
      } else {
        setError(true)
        setPasscode('')
      }
    }
  }

  useEffect(() => {
    if (passcode.length === 4) {
      verifyPasscode()
    }
  }, [passcode])

  // 웹뷰 환경에서는 자동으로 네이티브 패스코드 화면을 호출하지 않음
  // 네이티브 앱에서 자체적으로 패스코드를 확인하고 있을 가능성이 높음

  // 웹뷰 환경에서는 네이티브 패스코드 화면을 사용하므로 UI 렌더링 불필요
  if (isWebView()) {
    return (
      <div className='fixed inset-0 bg-white z-50 flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-gray-600'>패스코드 확인 중...</p>
        </div>
      </div>
    )
  }

  // 웹 환경에서만 패스코드 입력 UI 표시
  return (
    <div className='fixed inset-0 bg-white z-50 flex flex-col items-center justify-center p-4'>
      <div className='w-full max-w-sm'>
        <h2 className='text-2xl font-bold text-center mb-8'>패스코드 입력</h2>

        <div className='flex justify-center gap-4 mb-8'>
          {[0, 1, 2, 3].map(index => (
            <div
              key={index}
              className={`w-12 h-12 rounded-full border-2 ${
                passcode.length > index ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
              } ${error && index === 0 ? 'animate-shake' : ''}`}
            />
          ))}
        </div>

        {error && <p className='text-red-500 text-center mb-4'>잘못된 패스코드입니다. 다시 시도해주세요.</p>}

        <input
          type='number'
          inputMode='numeric'
          value={passcode}
          onChange={e => handlePasscodeInput(e.target.value)}
          className='sr-only'
          autoFocus
          maxLength={4}
        />

        <div className='grid grid-cols-3 gap-4'>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button
              key={num}
              onClick={() => handlePasscodeInput(passcode + num)}
              className='w-full h-16 text-2xl font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors'
            >
              {num}
            </button>
          ))}
          <div />
          <button
            onClick={() => handlePasscodeInput(passcode + '0')}
            className='w-full h-16 text-2xl font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors'
          >
            0
          </button>
          <button
            onClick={() => setPasscode(passcode.slice(0, -1))}
            className='w-full h-16 text-lg font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors'
          >
            ←
          </button>
        </div>
      </div>
    </div>
  )
}
